import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Bell, FileCheck2, FolderOpen, Lightbulb, ListTodo, MessageSquareText, ShieldCheck, WifiOff } from "lucide-react";
import { AnimatedNumber } from "@/components/aion/AnimatedNumber";
import { AionVisualPresence } from "@/components/aion/AionVisualPresence";
import { AionHolographicFace } from "@/components/aion/AionHolographicFace";
import { Button } from "@/components/ui/button";
import { AionEmptyState } from "@/components/aion/AionEmptyState";
import { AionSectionCard } from "@/components/aion/AionSectionCard";
import { getVoiceStateCopy, normalizeVoiceState, VOICE_STATES } from "@/constants/voiceRuntime";
import { getLocalCoreStatus } from "@/services/api";

const HOME_VOICE_PROFILE_LOCK_STATES = new Set([
  VOICE_STATES.LISTENING,
  VOICE_STATES.SPEECH_DETECTED,
  VOICE_STATES.TRANSCRIBING,
  VOICE_STATES.THINKING,
  VOICE_STATES.EXECUTING,
  VOICE_STATES.RESPONDING,
  VOICE_STATES.SPEAKING,
]);

export const shouldLockHomeVoiceProfile = (voicePending, voiceState) => (
  Boolean(voicePending) || HOME_VOICE_PROFILE_LOCK_STATES.has(normalizeVoiceState(voiceState))
);

export const localCoreStatusLabel = (status) => {
  if (status?.available === false) return "Local Core nicht verbunden";
  if (!status?.connectivity) return "Local Core Status wird geladen";
  return status.connectivity.internet_online ? "Local Core online" : "Local Core arbeitet gerade offline";
};

const greetingByHour = () => {
  const hour = new Date().getHours();
  if (hour < 11) return "Guten Morgen";
  if (hour < 18) return "Guten Tag";
  return "Guten Abend";
};

const attentionCount = (home, notifications) => (home?.open_tasks?.length || 0) + (home?.open_decisions?.length || 0) + (notifications || []).filter((item) => !item.read).length;

const formatContextTime = (value) => {
  if (!value) return "Kontext gespeichert";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const localizeGeneratedCopy = (value) => {
  if (!value) return value;

  const exact = {
    "Review open tasks and assign the next action.": "Prüfe offene Aufgaben und lege den nächsten Schritt fest.",
  };

  return exact[value] || value;
};

const listOrEmpty = (items, renderItem, emptyTitle, emptyText, testId) => {
  if (!items?.length) {
    return <AionEmptyState title={emptyTitle} description={emptyText} testId={testId} />;
  }
  return <div className="aion-list" data-testid={`${testId}-list`}>{items.map(renderItem)}</div>;
};

export const HomeView = ({ home, projectIntelligence = null, userName, registeredDevices = [], scenes = [], assistantVoiceProfile = "female", onVoiceProfileSave, voiceStatus, voicePending, compactActionBar, compactHomeStatus, localCoreStatus, onGoToConversations, onGoToDecisions, onGoToMore, onGoToNotifications, onGoToProjects, onOpenMoreSection, onGoToToday, onGoToMemory, onGoToTrustCenter, trustHomeCard, voiceDisplay, voiceDisplayState, voicePartialTranscript, voiceState, wakeWordSupported, voiceCaptureSupported = true, audioInputLevel = 0, audioOutputLevel = 0 }) => {
  const localCoreQuery = useQuery({
    queryKey: ["aion-home-local-core-status"],
    queryFn: () => getLocalCoreStatus(null),
    refetchInterval: 8000,
    retry: false,
  });
  const resolvedLocalCoreStatus = localCoreStatus || localCoreQuery.data || null;
  const notifications = home?.notifications || [];
  const projectSummary = projectIntelligence?.summary || {};
  const projectPriorities = projectIntelligence?.top_priorities || [];
  const activeProjects = home?.active_projects || [];
  const recentMemories = home?.recent_memories || [];
  const risks = home?.risks || [];
  const opportunities = home?.opportunities || [];
  const openTasks = home?.open_tasks || [];
  const normalizedVoiceState = normalizeVoiceState(voiceState);
  const voiceProfileLocked = shouldLockHomeVoiceProfile(voicePending, normalizedVoiceState);
  const presenceCopy = getVoiceStateCopy(normalizedVoiceState);
  const latestTranscript = voiceDisplay?.transcript || voicePartialTranscript || "";
  const latestResponse = voiceDisplay?.response || "";
  const avatarName = assistantVoiceProfile === "male" ? "ORION" : "ASTRA";
  const voiceConfigured = voiceStatus?.voice_ready === true && voiceStatus?.stt_ready === true && voiceStatus?.tts_ready === true;
  const voiceLabel = !voiceConfigured ? "Voice nicht verbunden" : normalizedVoiceState === "IDLE" ? "Voice konfiguriert" : voiceDisplayState;
  const deviceCountFor = (types) => registeredDevices.filter(device => types.includes(String(device.type || device.device_type || device.metadata?.category || "").toLowerCase())).length;
  const cameraCount = deviceCountFor(["camera", "ip_camera"]);
  const robotCount = deviceCountFor(["robot", "robot_vacuum", "vacuum"]);
  const vehicleCount = registeredDevices.filter(device => device.metadata?.category === "vehicle_obd2").length;
  const protectionLabel = trustHomeCard?.aion_stop_active ? "STOP" : trustHomeCard?.guardian_status || "Nicht geprüft";

  return (
    <div className="aion-view aion-home-grid" data-testid="home-view">
      <section className="aion-pro-dashboard" data-testid="aion-pro-dashboard">
        <div className="aion-pro-hero">
          <div className="aion-pro-hero-copy">
            <p className="aion-pro-kicker">AION · DEIN ZUHAUSE</p>
            <h1>{greetingByHour()}<br /><strong>{userName || home?.user_name || "Willkommen"}</strong></h1>
            <p>Ich bin AION. Wie kann ich dir heute helfen?</p>
            <div className="aion-pro-quick-actions">
              <button type="button" onClick={() => onOpenMoreSection?.("devices")}><Lightbulb size={18} /> Licht & Geräte</button>
              <button type="button" onClick={() => onOpenMoreSection?.("energy")}><span>⚡</span> Energie</button>
              <button type="button" onClick={onGoToTrustCenter}><ShieldCheck size={18} /> Sicherheit</button>
              <button type="button" onClick={onGoToToday}><ListTodo size={18} /> Aufgaben</button>
              <button type="button" onClick={onGoToConversations} data-testid="home-open-conversations"><MessageSquareText size={18} /> Gespräche</button>
            </div>
          </div>

          <div className="aion-pro-assistant-stage" data-testid="home-assistant-stage">
            <button
              type="button"
              className={`aion-assistant-selector aion-assistant-selector-female ${assistantVoiceProfile === "female" ? "active" : ""}`}
              aria-pressed={assistantVoiceProfile === "female"}
              disabled={voiceProfileLocked}
              onClick={() => onVoiceProfileSave?.("female")}
              data-testid="home-assistant-select-female"
            >
              <span className="aion-assistant-selector-orb"><AionHolographicFace profile="female" expression="warm" /></span>
              <span><strong>ASTRA</strong><small>Weibliche Stimme</small></span>
              <span className="aion-assistant-selector-state">{assistantVoiceProfile === "female" ? "AKTIV" : voiceProfileLocked ? "WARTEN" : "WÄHLEN"}</span>
            </button>

            <div className="aion-pro-avatar-card aion-pro-avatar-card-center">
              <AionVisualPresence
                state={voiceConfigured ? voiceState : "ERROR"}
                audioInputLevel={audioInputLevel}
                audioOutputLevel={audioOutputLevel}
                statusLabel={voiceLabel}
                avatarProfile={assistantVoiceProfile}
                title={avatarName}
                subtitle={latestTranscript ? `Du: ${latestTranscript}` : voiceStatus?.privacy?.listening ? "Ich höre dir zu …" : "Frag mich etwas."}
              />
              <div className="aion-assistant-question" data-testid="home-assistant-question">
                <span>FRAG MICH ETWAS</span>
                <strong>{voiceStatus?.privacy?.listening ? "Ich höre dir zu …" : latestResponse || "Wie kann ich dir helfen?"}</strong>
                <small>{voiceStatus?.privacy?.policy_blocked
                  ? "Mikrofon ist im Trust Center gesperrt. Nutze Texteingabe."
                  : wakeWordSupported
                    ? "Sag „AION …“ oder nutze das Mikrofon."
                    : voiceCaptureSupported
                      ? "Nutze Mikrofon oder Texteingabe."
                      : "Nutze Texteingabe; Mikrofon ist in diesem Browser nicht verfügbar."}</small>
              </div>
            </div>

            <button
              type="button"
              className={`aion-assistant-selector aion-assistant-selector-male ${assistantVoiceProfile === "male" ? "active" : ""}`}
              aria-pressed={assistantVoiceProfile === "male"}
              disabled={voiceProfileLocked}
              onClick={() => onVoiceProfileSave?.("male")}
              data-testid="home-assistant-select-male"
            >
              <span className="aion-assistant-selector-orb"><AionHolographicFace profile="male" expression="calm" /></span>
              <span><strong>ORION</strong><small>Männliche Stimme</small></span>
              <span className="aion-assistant-selector-state">{assistantVoiceProfile === "male" ? "AKTIV" : voiceProfileLocked ? "WARTEN" : "WÄHLEN"}</span>
            </button>
          </div>
        </div>

        <div className="aion-pro-overview-grid">
          <article className="aion-pro-panel aion-pro-home-panel">
            <div className="aion-pro-card-heading"><span>Dein Zuhause</span><span className="aion-status-chip">{compactHomeStatus.deviceCount ? `${compactHomeStatus.deviceCount} Geräte` : "0 Geräte"}</span></div>
            <div className="aion-pro-home-visual">
              <div className="aion-pro-house-glow">AION HOME</div>
              <p>{compactHomeStatus.headline}</p>
            </div>
            <div className="aion-pro-stat-row">
              <div><span>Räume</span><strong>{compactHomeStatus.roomCount}</strong></div>
              <div><span>Geräte aktiv</span><strong>{compactHomeStatus.activeDevices}</strong></div>
              <div><span>Leistung</span><strong>{compactHomeStatus.currentPower}</strong></div>
              <div><span>Schutz</span><strong>{protectionLabel}</strong></div>
            </div>
          </article>

          <article className="aion-pro-panel">
            <div className="aion-pro-card-heading"><span>Heute</span><button type="button" onClick={onGoToToday}>Öffnen →</button></div>
            <div className="aion-pro-mini-list">
              {[...(home?.morning_brief?.top_priorities || []), ...openTasks.map((item) => item.title)].slice(0,4).map((item,index)=><div key={`${item}-${index}`}><span>○</span><strong>{localizeGeneratedCopy(item)}</strong></div>)}
              {!openTasks.length && !(home?.morning_brief?.top_priorities || []).length ? <div><span>✓</span><strong>Keine dringenden Aufgaben.</strong></div> : null}
            </div>
          </article>

          <article className="aion-pro-panel">
            <div className="aion-pro-card-heading"><span>Erkenntnisse</span><button type="button" onClick={onGoToMemory}>Memory →</button></div>
            <div className="aion-pro-mini-list">
              <div><ShieldCheck size={16}/><strong>{trustHomeCard?.headline || "Schutzstatus wird geladen."}</strong></div>
              {opportunities[0]?.title ? <div><Lightbulb size={16}/><strong>{opportunities[0].title}</strong></div> : null}
              <div><Bell size={16}/><strong>{notifications[0]?.title || "Keine neuen Hinweise."}</strong></div>
            </div>
          </article>
        </div>

        <div className="aion-pro-room-strip">
          <button type="button" onClick={() => onOpenMoreSection?.("devices")}><span>WOHNEN</span><strong>Geräte & Räume</strong><small>{compactHomeStatus.activeDevices} aktiv</small></button>
          <button type="button" onClick={() => onOpenMoreSection?.("automations")}><span>SZENEN</span><strong>Automationen</strong><small>Szenen verwalten</small></button>
          <button type="button" onClick={() => onOpenMoreSection?.("energy")}><span>ENERGIE</span><strong>Verbrauch</strong><small>{compactHomeStatus.currentPower}</small></button>
          <button type="button" onClick={onGoToTrustCenter}><span>SECURITY</span><strong>Trust Center</strong><small>{protectionLabel}</small></button>
        </div>
      </section>

      <section className="aion-pro-dashboard" aria-label="Verbundene Bereiche">
        <div className="aion-pro-room-strip">
          <button type="button" onClick={() => onOpenMoreSection?.("cameras")}><span>KAMERAS</span><strong>{cameraCount} Kameras</strong><small>{cameraCount ? "Registriert · Status öffnen" : "Nicht verbunden"}</small></button>
          <button type="button" onClick={() => onOpenMoreSection?.("vehicle")}><span>FAHRZEUG</span><strong>{vehicleCount} Fahrzeuge</strong><small>{vehicleCount ? "Registriert · Telemetrie öffnen" : "Nicht verbunden"}</small></button>
          <button type="button" onClick={() => onOpenMoreSection?.("robotics")}><span>ROBOTER</span><strong>{robotCount} Roboter</strong><small>{robotCount ? "Registriert · Status öffnen" : "Nicht verbunden"}</small></button>
          <button type="button" onClick={() => onOpenMoreSection?.("scenes")}><span>SZENEN</span><strong>{scenes.length} Szenen</strong><small>Szenen öffnen</small></button>
        </div>
        <div className="aion-pro-overview-grid">
          <article className="aion-pro-panel"><div className="aion-pro-card-heading"><span>Termine</span><button type="button" onClick={() => onOpenMoreSection?.("appointments")}>Öffnen →</button></div><p>Keine Kalenderdaten verfügbar.</p></article>
          <article className="aion-pro-panel"><div className="aion-pro-card-heading"><span>Kommunikation</span><button type="button" onClick={() => onOpenMoreSection?.("communication")}>Öffnen →</button></div><p>Keine Nachrichtenquelle verbunden.</p></article>
          <article className="aion-pro-panel"><div className="aion-pro-card-heading"><span>Wetter</span></div><p>Keine Wetterdaten verfügbar.</p></article>
        </div>
      </section>

      <AionSectionCard
        title="Heutiger Fokus"
        eyebrow="HEUTE"
        subtitle="Die wichtigsten Punkte aus Brief, offenen Aufgaben und Entscheidungen."
        testId="home-focus-card"
      >
        {listOrEmpty(
          [...(home?.morning_brief?.top_priorities || []), ...openTasks.map((item) => item.title), ...(home?.open_decisions || []).map((item) => item.title)].slice(0, 5),
          (item, index) => <div className="aion-list-row" key={`${item}-${index}`} data-testid={`home-focus-item-${index}`}><ListTodo className="aion-list-icon" aria-hidden="true" /><span>{localizeGeneratedCopy(item)}</span></div>,
          "Noch kein Tagesfokus.",
          "AION hat aktuell keine priorisierten Punkte für heute gespeichert.",
          "home-focus-empty"
        )}
      </AionSectionCard>

      <AionSectionCard
        title="Offene Punkte"
        eyebrow="AUFMERKSAMKEIT"
        subtitle="Aufgaben und offene Entscheidungen, die gerade nicht untergehen sollten."
        action={<Button variant="outline" onClick={onGoToDecisions} data-testid="home-open-decisions"><FileCheck2 className="aion-button-icon" aria-hidden="true" /> Decision Review</Button>}
        testId="home-open-items-card"
      >
        {listOrEmpty(
          [...openTasks.map((item) => ({ label: item.title, meta: item.classification || "task" })), ...(home?.open_decisions || []).map((item) => ({ label: item.title, meta: item.review_status || "decision" }))].slice(0, 6),
          (item, index) => <div className="aion-list-row" key={`${item.label}-${index}`} data-testid={`home-open-item-${index}`}><strong>{item.label}</strong><small>{item.meta === "task" ? "Aufgabe" : item.meta === "decision" ? "Entscheidung" : item.meta === "open" ? "Offen" : item.meta}</small></div>,
          "Keine offenen Punkte.",
          "AION hat aktuell keine offenen Aufgaben oder Entscheidungs-Flags erkannt.",
          "home-open-items-empty"
        )}
      </AionSectionCard>

      <AionSectionCard
        title="Unternehmen"
        eyebrow="MULTI-PROJECT"
        subtitle="Projektübergreifende Analyse aus den verbundenen Systemen."
        action={<Button variant="outline" onClick={onGoToProjects} data-testid="home-open-project-intelligence">Projektanalyse</Button>}
        testId="home-project-intelligence-card"
      >
        <div className="aion-inline-stats" data-testid="home-project-intelligence-summary">
          <div className="aion-inline-stat"><span>Projekte</span><strong>{projectSummary.total_projects ?? 0}</strong></div>
          <div className="aion-inline-stat"><span>Verbunden</span><strong>{projectSummary.configured_projects ?? 0}</strong></div>
          <div className="aion-inline-stat"><span>Achtung</span><strong>{projectSummary.attention_projects ?? 0}</strong></div>
          <div className="aion-inline-stat"><span>Offene Aufgaben</span><strong>{projectSummary.open_tasks ?? 0}</strong></div>
        </div>
        {projectPriorities.length ? (
          <div className="aion-list" data-testid="home-project-priorities">
            {projectPriorities.slice(0, 3).map((item, index) => (
              <div className="aion-list-row" key={item.project_key || index}>
                <AlertTriangle className="aion-list-icon" aria-hidden="true" />
                <div>
                  <strong>{item.project_name || item.project_key}</strong>
                  <small>{(item.top_alert || item.top_blocker || item.top_risk || item.reasons?.join(" · "))}</small>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <AionEmptyState
            title="Keine kritischen Projekt-Hinweise."
            description="AION zeigt hier nur echte Warnungen und Prioritäten aus verbundenen Projekten."
            testId="home-project-intelligence-empty"
          />
        )}
      </AionSectionCard>

      <AionSectionCard
        title="Aktive Projekte"
        eyebrow="PROJEKTE"
        subtitle="Deine laufenden Projekte im Überblick."
        action={<Button variant="outline" onClick={onGoToProjects} data-testid="home-open-projects">Projekt-Workspace</Button>}
        testId="home-projects-card"
      >
        {listOrEmpty(
          activeProjects,
          (item, index) => <div className="aion-list-row" key={item.id || `${item.title}-${index}`} data-testid={`home-project-item-${index}`}><FolderOpen className="aion-list-icon" aria-hidden="true" /><div><strong>{item.title}</strong><small>{item.summary || "Ohne Zusammenfassung"}</small></div></div>,
          "Noch keine aktiven Projekte.",
          "Gespeicherte Projekte erscheinen hier.",
          "home-projects-empty"
        )}
      </AionSectionCard>

      {risks.length > 0 ? <AionSectionCard
        title="Risiken"
        eyebrow="RISIKEN"
        subtitle="Nur echte Muster- oder Widerspruchssignale aus dem vorhandenen Intelligence-System."
        testId="home-risks-card"
      >
        {listOrEmpty(
          risks,
          (item, index) => <div className="aion-list-row" key={item.id || index} data-testid={`home-risk-item-${index}`}><AlertTriangle className="aion-list-icon" aria-hidden="true" /><div><strong>{item.pattern_type}</strong><small>{item.summary}</small></div></div>,
          "Keine wichtigen Risiken erkannt.",
          "AION hat aktuell keine kritischen Muster für dich erkannt.",
          "home-risks-empty"
        )}
      </AionSectionCard> : null}

      {opportunities.length > 0 ? <AionSectionCard
        title="Chancen"
        eyebrow="CHANCEN"
        subtitle="Verbindungen und Hinweise aus dem bestehenden Connections-Layer."
        testId="home-opportunities-card"
      >
        {listOrEmpty(
          opportunities,
          (item, index) => <div className="aion-list-row" key={item.id || index} data-testid={`home-opportunity-item-${index}`}><Lightbulb className="aion-list-icon" aria-hidden="true" /><div><strong>{item.title}</strong><small>{item.description}</small></div></div>,
          "Noch keine klaren Chancen erkannt.",
          "Sobald AION echte Zusammenhänge mit hoher Sicherheit sieht, erscheinen sie hier.",
          "home-opportunities-empty"
        )}
      </AionSectionCard> : null}

      <AionSectionCard
        title="Letzte Erinnerungen"
        eyebrow="MEMORY"
        subtitle="Was AION zuletzt als wichtig im Kontext gespeichert hat."
        testId="home-recent-memories-card"
      >
        {listOrEmpty(
          recentMemories,
          (item, index) => (
            <button type="button" className="aion-memory-row" key={`${item.recorded_at || index}-${index}`} onClick={onGoToMemory} data-testid={`home-recent-memory-${index}`}>
              <div className="aion-list-copy" data-testid={`home-recent-memory-copy-${index}`}>
                <strong>{item.summary || item.title || item.type || "Erinnerung"}</strong>
                <small>{formatContextTime(item.recorded_at)}</small>
              </div>
            </button>
          ),
          "Noch keine aktuellen Erinnerungen.",
          "AION zeigt hier echte Kontexte, sobald Gespräche und Entscheidungen gespeichert wurden.",
          "home-memories-empty"
        )}
      </AionSectionCard>

      <AionSectionCard
        title="Hinweise"
        eyebrow="HINWEISE"
        subtitle="Was heute deine Aufmerksamkeit braucht."
        action={<Button variant="outline" onClick={onGoToNotifications} data-testid="home-open-notifications"><Bell className="aion-button-icon" aria-hidden="true" /> Alle Hinweise</Button>}
        testId="home-notifications-card"
      >
        {listOrEmpty(
          notifications,
          (item, index) => <div className="aion-list-row" key={item.id || index} data-testid={`home-notification-item-${index}`}><Bell className="aion-list-icon" aria-hidden="true" /><div><strong>{item.title}</strong><small>{item.message}</small></div></div>,
          "Noch keine Benachrichtigungen.",
          "AION zeigt hier nur echte neue Hinweise, keine Demo-Daten.",
          "home-notifications-empty"
        )}
      </AionSectionCard>

      <AionSectionCard
        title="Zuhause-Status"
        eyebrow="ZUHAUSE"
        subtitle="Räume, Geräte und Energie im Überblick."
        action={<Button variant="outline" onClick={onGoToMore} data-testid="home-open-home-devices-button">Zuhause öffnen</Button>}
        className="aion-home-status-card"
        testId="home-status-card"
      >
        <div className="aion-compact-status-grid" data-testid="home-compact-status-grid">
          <div className="aion-compact-status-box" data-testid="home-active-devices-box"><span>Geräte aktiv</span><strong><AnimatedNumber value={compactHomeStatus.activeDevices} dataTestId="home-active-devices-value" /></strong></div>
          <div className="aion-compact-status-box" data-testid="home-room-count-box"><span>Räume</span><strong><AnimatedNumber value={compactHomeStatus.roomCount} dataTestId="home-room-count-value" /></strong></div>
          <div className="aion-compact-status-box" data-testid="home-energy-box"><span>Energie</span><strong><AnimatedNumber value={compactHomeStatus.currentPowerValue} decimals={1} suffix=" W" fallback="—" dataTestId="home-energy-value" /></strong></div>
        </div>
        <div className="aion-home-status-inline" data-testid="home-status-inline">
          <ShieldCheck className="aion-list-icon" aria-hidden="true" />
          <span data-testid="home-status-inline-text">{compactHomeStatus.headline}</span>
        </div>
        <div className="aion-home-status-inline" data-testid="home-local-core-inline">
          <WifiOff className="aion-list-icon" aria-hidden="true" />
          <span data-testid="home-local-core-inline-text">
            {localCoreStatusLabel(resolvedLocalCoreStatus)}
          </span>
        </div>
      </AionSectionCard>
    </div>
  );
};
